
import Vue from "vue";
import Component from "vue-class-component";
import FileUploader from "@/components/FileUploader.vue";
import DropdownSelector from "@/components/DropdownSelector.vue";
import Files from "@/components/Files.vue";
import CheckboxInput from "@/features/shared/components/CheckboxInput.vue";
import FormLayout from "@/features/personProfile/components/FormLayout.vue";
import LabelOptional from "@/features/shared/components/LabelOptional.vue";

import {
    AddShipRepairPracticeRequest,
    OrganizationOption,
    PersonOption,
    PositionOption,
    PositionsClient,
    PracticeKind,
    PracticeType,
    PracticePeriod,
    PracticesClient, ResultError,
    ShipRepairPracticeActions,
    FindOverlappingPracticesRequest,
    IFindOverlappingPracticesRequest
} from "@/api-client";
import PracticePeriods from "@/features/documents/practices/components/PracticePeriods.vue";
import HeadingGroup from "@/features/shared/components/HeadingGroup.vue";
import {Prop} from "vue-property-decorator";
import Loader from "@/features/shared/components/Loader";
import toasts from "@/components/Toasts";
import moment from "moment";
import userInfo from "@/features/shared/components/UserInfo";

@Component({
    components: {
        CheckboxInput,
        DropdownSelector,
        Files,
        FileUploader,
        FormLayout,
        HeadingGroup,
        LabelOptional,
        PracticePeriods,
    }
})
export default class ShipRepairPracticeUpsertForm extends Vue {
    @Prop({required: true})
    seafarerId!: string;

    @Prop({required: true})
    model!: AddShipRepairPracticeRequest;

    @Prop({required: true})
    actions!: ShipRepairPracticeActions;

    @Prop({required: true})
    submitCallback!: (model: AddShipRepairPracticeRequest) => Promise<void>;

    validationErrors = ResultError.createEmpty();

    warningMatching = false;

    isConfirmerNameInsertedManually: boolean =
        !this.model.confirmerId && !!this.model.confirmer;

    positionOptions: PositionOption[] = [];
    organizationOptions: OrganizationOption[] = [];
    confirmerOptions: PersonOption[] = [];

    practiceKindOptions = [
        {text: this.$ls.Documents_Practices_PracticeKind_Educational, value: PracticeKind.Educational},
        {text: this.$ls.Documents_Practices_PracticeKind_Work, value: PracticeKind.Work},
    ];

    fileLoader = new Loader();
    submitLoader = new Loader();
    isCreated = false;
    isSeafarer = userInfo.seafarerId === this.seafarerId;

    async created() {
        await Promise.all(
            [
                new PositionsClient().activePositionOptions().then(x => this.positionOptions = x),
                new PracticesClient().shipRepairOrganizationOptions().then(x => this.organizationOptions = x.organizationOptions.filter(o => o.active)),
            ]
        );

        this.setConfirmerOptions();

        if (this.actions.canStartEnteringEducational && !this.actions.canStartEnteringWorking) {
            this.model.kind = PracticeKind.Educational;
        }
        if (this.actions.canStartEnteringWorking && !this.actions.canStartEnteringEducational) {
            this.model.kind = PracticeKind.Work;
        }

        if (!this.model.practicePeriods) {
            this.model.practicePeriods = [];
        }

        this.$watch(() => this.model, () => {
            this.validationErrors.clearKey("practicePeriods");
            if(this.model.issuedOn && moment().startOf("day").diff(this.model.issuedOn) < 0){
                this.validationErrors.clearKey("issuedOn");
                this.validationErrors.appendMessage(this.$ls.Global_IssuedOn_In_The_Future,"issuedOn");
            }
            else {
                this.validationErrors.clearKey("issuedOn");
                this.validationErrors.clear();
                if (this.model.issuedOn && this.model.practicePeriods.length > 0) {
                    this.model.practicePeriods.forEach(period => {
                        if (moment(period.startedOn).startOf("day").diff(this.model.issuedOn) > 0) {
                            if(this.validationErrors.hasMessages("practicePeriods")){
                                this.validationErrors.clearKey("practicePeriods");
                            }
                            this.validationErrors.appendMessage(this.$ls.Practice_Validation_PeriodsAfterIssuedOn, "practicePeriods");
                        }
                        if (moment(period.endedOn).startOf("day").diff(this.model.issuedOn) > 0) {
                            if(this.validationErrors.hasMessages("practicePeriods")){
                                this.validationErrors.clearKey("practicePeriods");
                            }
                            this.validationErrors.appendMessage(this.$ls.Practice_Validation_PeriodsAfterIssuedOn, "practicePeriods");
                        }
                    });
                }
            }
        },{deep:true});
        this.$watch(
            () => this.isConfirmerNameInsertedManually,
            () => {
                if (this.isConfirmerNameInsertedManually) {
                    this.model.confirmerId = null;
                } else {
                    this.setConfirmerOptions();
                }
            }
        );

        this.isCreated = true;
        this.warningMatching = false;
    }

    async submit() {
        if(!this.validationErrors.isEmpty){
            return;
        }
      this.submitLoader.startLoading();
        this.validationErrors.clear();
        await this.submitCallback(this.model)
            .then(async () => {
                toasts.success(this.$ls.Documents_Qualifications_Toast_Success_DocumentSaving);
            })
            .catch((error: ResultError) => this.setValidationErrors(error, this.$ls.Global_Toast_FormSaveFailure));
      this.submitLoader.endLoading();
    }

    private addPracticePeriod(practicePeriod: PracticePeriod) {
        if (this.model.positionId) {
            const requestModel: IFindOverlappingPracticesRequest = {
                positionId: this.model.positionId,
                practiceType: PracticeType.ShipRepair,
                organizationId: this.model.organizationId,
                practicePeriod: practicePeriod
            }
            Promise.all([
                new PracticesClient().hasOverlap(this.seafarerId, new FindOverlappingPracticesRequest(requestModel))
            ]).then(value => {
                this.warningMatching = value[0];
            });
        }

        this.model.practicePeriods.push(practicePeriod);
    }

    private removePracticePeriod(practicePeriod: PracticePeriod) {
        this.$delete(
            this.model.practicePeriods,
            this.model.practicePeriods.indexOf(practicePeriod)
        );
    }

    private setConfirmerOptions() {
        if (!this.model.organizationId)
            return;

        const organization = this.organizationOptions.find(o => o.value == this.model.organizationId);
        this.confirmerOptions = organization ? organization.personOptions : []

        this.isConfirmerNameInsertedManually = this.hasNoConfirmerOptions;
    }

    private translatePracticeKind(kind: PracticeKind | undefined | null): string {
        if (!kind)
            return "";

        return this.practiceKindOptions.find((o) => o.value == kind)!.text
    }

    private get hasNoConfirmerOptions() {
        return this.confirmerOptions.length === 0;
    }

    private get hasAnyConfirmerOptions() {
        return !this.hasNoConfirmerOptions;
    }
}
